import { theme } from '@traba/theme'
import { useId } from 'react'
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'
import {
  BRAND_ROWS,
  NETWORK_WEEK,
  NORTHWIND_TRAJECTORY,
  PACKAGE_WEIGHT_SERIES,
  SHIP_COST_SERIES,
  ZONE7_SHARE_SERIES,
  type CascadeBrandRow,
  type MixShiftSeriesPoint,
} from './data'
import {
  CHART_AXIS_PROPS,
  CHART_PALETTE,
  ChartTooltip,
  DashboardHeader,
  DiscussWithNeoButton,
  HeroCard,
  HeroVerdict,
  InlineBar,
  InsightCallout,
  MetricCard,
  MetricRow,
  SectionCard,
  SectionSubtitle,
  SectionTitle,
  ShellRoot,
  TwoColRow,
} from './DashboardShell'

// ---------------------------------------------------------------------------
// Local helpers + styled bits
// ---------------------------------------------------------------------------

const QUADRANT_COLORS: Record<CascadeBrandRow['status'], string> = {
  good: CHART_PALETTE.good,
  warning: CHART_PALETTE.warning,
  critical: CHART_PALETTE.critical,
}

const MIN_BUBBLE_R = 10
const MAX_BUBBLE_R = 36

function scaleBubble(orders: number, allOrders: number[]): number {
  const minO = Math.min(...allOrders)
  const maxO = Math.max(...allOrders)
  if (maxO === minO) {
    return (MIN_BUBBLE_R + MAX_BUBBLE_R) / 2
  }
  const t = (orders - minO) / (maxO - minO)
  return MIN_BUBBLE_R + t * (MAX_BUBBLE_R - MIN_BUBBLE_R)
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`
  }
  if (value >= 1_000) {
    return `$${Math.round(value / 1_000)}K`
  }
  return `$${value}`
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

const NORTHWIND_HISTORICAL = NORTHWIND_TRAJECTORY.map((p) => ({
  monthLabel: p.monthLabel,
  historical: p.projected ? null : p.marginPct,
  projected: p.projected ? p.marginPct : null,
}))

const MAX_BRAND_MARGIN = 20

// ---------------------------------------------------------------------------
// Brand list (replaces the spreadsheet table)
// ---------------------------------------------------------------------------

const BrandList = styled.div`
  display: flex;
  flex-direction: column;
`

const BrandRow = styled.div<{ $emphasize: boolean }>`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.5fr 1.4fr;
  align-items: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.xs}px ${theme.space.xs}px;
  border-radius: 8px;
  background: ${({ $emphasize }) =>
    $emphasize ? theme.colors.Red10 : 'transparent'};
  position: relative;

  & + & {
    margin-top: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: ${({ $emphasize }) => ($emphasize ? '3px' : '0')};
    background: ${theme.colors.Red60};
    border-radius: 999px;
  }
`

const BrandName = styled.div<{ $emphasize: boolean }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: ${({ $emphasize }) => ($emphasize ? 700 : 500)};
  color: ${theme.colors.MidnightBlue};
  display: flex;
  align-items: center;
  gap: ${theme.space.xxs}px;
  padding-left: ${({ $emphasize }) => ($emphasize ? '12px' : '0')};
`

const StatusDot = styled.span<{ $tone: 'good' | 'warning' | 'critical' }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red60
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow70
    }
    return theme.colors.Green70
  }};
  flex-shrink: 0;
`

const BrandRevenue = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey60};
  text-align: right;

  strong {
    display: block;
    color: ${theme.colors.MidnightBlue};
    font-size: 14px;
    font-weight: 600;
  }
`

const BrandTrend = styled.div<{ $tone: 'good' | 'warning' | 'critical' }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red80
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow80
    }
    return theme.colors.Grey60
  }};
  text-align: right;
`

const BrandListHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.5fr 1.4fr;
  gap: ${theme.space.sm}px;
  padding: 0 ${theme.space.xs}px ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${theme.colors.Grey20};
  margin-bottom: ${theme.space.xs}px;

  & > div:nth-child(2),
  & > div:nth-child(4) {
    text-align: right;
  }
`

// ---------------------------------------------------------------------------
// Mix-shift drivers section
// ---------------------------------------------------------------------------

const MixShiftGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.sm}px;
`

const MixShiftRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 110px;
  align-items: center;
  gap: ${theme.space.xs}px;
`

const MixShiftLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.MidnightBlue};
  font-weight: 500;
`

const MixShiftDelta = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  color: ${theme.colors.Red80};
  font-weight: 600;
  text-align: right;
`

function Sparkline({
  data,
  color,
}: {
  data: MixShiftSeriesPoint[]
  color: string
}) {
  // Use useId to scope the gradient per Sparkline instance — otherwise three
  // sparklines using the same color all emit identical gradient ids and the
  // SVG document ends up with duplicate ids (still renders, but bad hygiene
  // and breaks the moment a sparkline uses a different color elsewhere).
  const reactId = useId()
  const gradientId = `spark-${reactId.replace(/:/g, '')}`
  return (
    <div style={{ width: '100%', height: 40 }}>
      <ResponsiveContainer>
        <AreaChart
          data={data}
          margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recommended actions
// ---------------------------------------------------------------------------

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs}px;
`

const ActionRow = styled.div<{ $recommended?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.sm}px;
  background: ${({ $recommended }) =>
    $recommended ? theme.colors.Green10 : theme.colors.Grey10};
  border: 1px solid
    ${({ $recommended }) =>
      $recommended ? theme.colors.Green40 : theme.colors.Grey20};
  border-radius: ${theme.border.radius};
`

const ActionTitle = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
  margin-bottom: 4px;
`

const ActionDescription = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey60};
  line-height: 1.45;
`

const ActionLabel = styled.div<{ $tone: 'good' | 'neutral' }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ $tone }) =>
    $tone === 'good' ? theme.colors.Green80 : theme.colors.Grey60};
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomerPnlDashboard() {
  const allOrders = BRAND_ROWS.map((b) => b.openOrdersWeek)
  const scatterData = BRAND_ROWS.map((b) => ({
    name: b.name,
    x: b.revenueSharePct,
    y: b.marginPct,
    z: b.openOrdersWeek,
    status: b.status,
    r: scaleBubble(b.openOrdersWeek, allOrders),
  }))

  const ytdPacePct = Math.round(
    (NETWORK_WEEK.cumulativeContributionYTD /
      NETWORK_WEEK.contributionTargetYTD) *
      100,
  )

  return (
    <ShellRoot>
      <DashboardHeader
        title="Customer P&L Forecast"
        subtitle="Forward + historical per-customer profitability across 68 active brands. Updates every 4:00 AM ET from ShipHero open orders + NetSuite rate card + pick/pack productivity model."
        rightSlot={
          <DiscussWithNeoButton
            scriptId="customer-pnl-forecast"
            prompt="Walk me through the customer P&L forecast"
          />
        }
      />

      <HeroVerdict
        tone="critical"
        eyebrow="Margin trajectory · largest customer"
        headline="Northwind Apparel margin collapsed from 18% to 7.2% over six months"
        sub="$2.4M of annualized contribution already lost YTD. At current trajectory the brand crosses break-even by November — Cascade's largest customer by revenue turns unprofitable in 6 months."
      />

      <MetricRow>
        <MetricCard
          tone="good"
          label="Next-week contribution"
          value={`${formatCurrency(NETWORK_WEEK.forecastContribution)} · ${NETWORK_WEEK.contributionPct}%`}
          sub={`on ${formatCurrency(NETWORK_WEEK.forecastRevenue)} forecast revenue`}
        />
        <MetricCard
          tone="critical"
          label="Annualized at risk · Northwind"
          value="$2.4M"
          sub="vs Q3 2024 contract baseline"
        />
        <MetricCard
          tone="neutral"
          label="Brand portfolio"
          value={`${NETWORK_WEEK.brandsActive}`}
          sub={`top 10 = ${NETWORK_WEEK.topTenBrandRevenuePct}% of revenue`}
        />
        <MetricCard
          tone="neutral"
          label="Contribution YTD"
          value={formatCurrency(NETWORK_WEEK.cumulativeContributionYTD)}
          sub={`vs target ${formatCurrency(NETWORK_WEEK.contributionTargetYTD)} · ${ytdPacePct}% pace`}
        />
      </MetricRow>

      <InsightCallout
        tone="critical"
        glyph="●"
        text={
          <>
            <strong>
              Largest brand by revenue is below the 10% margin floor
            </strong>{' '}
            — Northwind Apparel at 7.2% after west-coast launch. Briar (#2) and
            Trailmix (#3) are healthy at 17.5% and 14.1%; the only other
            below-floor brands are tail accounts (Fernweh, Lumen).
          </>
        }
      />

      <HeroCard>
        <SectionTitle>Brand portfolio · revenue share × margin</SectionTitle>
        <SectionSubtitle>
          X axis: % of network revenue. Y axis: contribution margin %. Bubble
          size = weekly order volume. Bottom-right quadrant is the risk zone —
          high revenue concentration paired with low margin.
        </SectionSubtitle>
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 16, right: 24, bottom: 36, left: 24 }}>
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="x"
                type="number"
                domain={[0, 16]}
                tickFormatter={(v: number) => `${v}%`}
                label={{
                  value: 'Revenue share',
                  position: 'insideBottom',
                  offset: -16,
                  fill: CHART_PALETTE.axisText,
                  fontSize: 11,
                }}
              />
              <YAxis
                {...CHART_AXIS_PROPS}
                dataKey="y"
                type="number"
                domain={[0, MAX_BRAND_MARGIN]}
                tickFormatter={(v: number) => `${v}%`}
                label={{
                  value: 'Margin',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 8,
                  fill: CHART_PALETTE.axisText,
                  fontSize: 11,
                }}
              />
              <ReferenceLine
                y={10}
                stroke={CHART_PALETTE.neutral}
                strokeDasharray="3 5"
                label={{
                  value: '10% margin floor',
                  position: 'right',
                  fill: CHART_PALETTE.axisText,
                  fontSize: 10,
                }}
              />
              <ReferenceLine
                x={8}
                stroke={CHART_PALETTE.neutral}
                strokeDasharray="3 5"
              />
              <Tooltip
                cursor={{ stroke: theme.colors.Grey30 }}
                content={(props) => (
                  <ChartTooltip
                    active={props.active}
                    label={
                      props.payload && props.payload[0]
                        ? (props.payload[0].payload?.name as string)
                        : undefined
                    }
                    payload={
                      props.payload && props.payload[0]
                        ? [
                            {
                              name: 'Margin',
                              value: `${props.payload[0].payload?.y}%`,
                            },
                            {
                              name: 'Revenue share',
                              value: `${props.payload[0].payload?.x}%`,
                            },
                            {
                              name: 'Weekly orders',
                              value: formatCount(
                                props.payload[0].payload?.z as number,
                              ),
                            },
                          ]
                        : []
                    }
                  />
                )}
              />
              <Scatter
                data={scatterData}
                shape={(props: {
                  cx?: number
                  cy?: number
                  payload?: { r: number; status: CascadeBrandRow['status'] }
                }) => {
                  const { cx, cy, payload } = props
                  if (cx == null || cy == null || !payload) {
                    return <g />
                  }
                  const color = QUADRANT_COLORS[payload.status]
                  return (
                    <g>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={payload.r}
                        fill={color}
                        fillOpacity={0.55}
                        stroke={color}
                        strokeWidth={1.5}
                      />
                    </g>
                  )
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 32px',
            fontFamily: theme.typography.fontFamily,
            fontSize: 11,
            color: theme.colors.Grey60,
            marginTop: 8,
          }}
        >
          <div>↖ Niche profitable</div>
          <div style={{ textAlign: 'right' }}>↗ Star (high rev + margin)</div>
          <div>↙ Tail</div>
          <div
            style={{
              textAlign: 'right',
              color: theme.colors.Red60,
              fontWeight: 600,
            }}
          >
            ↘ Risk (high revenue, low margin)
          </div>
        </div>
      </HeroCard>

      <SectionCard>
        <SectionTitle>Top 8 brands · next week</SectionTitle>
        <SectionSubtitle>
          Margin bar shows contribution % against the 20% network ceiling. 6-mo
          trend column compares current margin to six months ago.
        </SectionSubtitle>
        <BrandListHeader>
          <div>Brand</div>
          <div>Revenue</div>
          <div>Margin</div>
          <div>6-mo trend</div>
        </BrandListHeader>
        <BrandList>
          {BRAND_ROWS.map((b) => {
            const isNorthwind = b.id === 'northwind'
            const delta = b.marginPct - b.marginPct6moAgo
            const trendTone: 'good' | 'warning' | 'critical' =
              delta <= -3 ? 'critical' : delta < 0 ? 'warning' : 'good'
            const marginBarPct = (b.marginPct / MAX_BRAND_MARGIN) * 100
            return (
              <BrandRow key={b.id} $emphasize={isNorthwind}>
                <BrandName $emphasize={isNorthwind}>
                  <StatusDot $tone={b.status} />
                  {b.name}
                </BrandName>
                <BrandRevenue>
                  <strong>{formatCurrency(b.revenueWeek)}</strong>
                  {formatCount(b.openOrdersWeek)} orders
                </BrandRevenue>
                <InlineBar
                  widthPct={marginBarPct}
                  label={`${b.marginPct.toFixed(1)}%`}
                  tone={b.status}
                />
                <BrandTrend $tone={trendTone}>
                  {b.marginPct6moAgo.toFixed(1)}% → {b.marginPct.toFixed(1)}%
                </BrandTrend>
              </BrandRow>
            )
          })}
        </BrandList>
      </SectionCard>

      <TwoColRow>
        <SectionCard>
          <SectionTitle>Northwind margin trajectory</SectionTitle>
          <SectionSubtitle>
            Solid: 6 months historical. Dashed: projection at current trend.
            Annualized contribution lost YTD: $2.4M.
          </SectionSubtitle>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <LineChart
                data={NORTHWIND_HISTORICAL}
                margin={{ top: 12, right: 32, bottom: 8, left: 0 }}
              >
                <defs>
                  <linearGradient id="trajGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={CHART_PALETTE.critical}
                      stopOpacity={0.18}
                    />
                    <stop
                      offset="100%"
                      stopColor={CHART_PALETTE.critical}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  {...CHART_AXIS_PROPS}
                  dataKey="monthLabel"
                  interval={0}
                />
                <YAxis
                  {...CHART_AXIS_PROPS}
                  domain={[0, 20]}
                  tickFormatter={(v: number) => `${v}%`}
                />
                <ReferenceLine
                  y={10}
                  stroke={CHART_PALETTE.neutral}
                  strokeDasharray="3 5"
                  label={{
                    value: '10% floor',
                    position: 'right',
                    fill: CHART_PALETTE.axisText,
                    fontSize: 10,
                  }}
                />
                <Tooltip
                  cursor={{ stroke: theme.colors.Grey30 }}
                  content={(props) => (
                    <ChartTooltip
                      active={props.active}
                      label={props.label as string | undefined}
                      payload={
                        props.payload
                          ? props.payload
                              .filter((p) => p.value != null)
                              .map((p) => ({
                                name:
                                  p.dataKey === 'historical'
                                    ? 'Historical'
                                    : 'Projected',
                                value:
                                  typeof p.value === 'number'
                                    ? `${p.value.toFixed(1)}%`
                                    : String(p.value),
                                color: CHART_PALETTE.critical,
                              }))
                          : []
                      }
                    />
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="historical"
                  stroke={CHART_PALETTE.critical}
                  strokeWidth={2.5}
                  dot={{ fill: CHART_PALETTE.critical, r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="projected"
                  stroke={CHART_PALETTE.critical}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{
                    fill: theme.colors.White,
                    stroke: CHART_PALETTE.critical,
                    r: 2.5,
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Mix-shift drivers · Northwind</SectionTitle>
          <SectionSubtitle>
            Three lines tracking the west-coast launch effect. All three
            correlate against the margin collapse.
          </SectionSubtitle>
          <MixShiftGrid>
            <MixShiftRow>
              <MixShiftLabel>Zone 7+ share</MixShiftLabel>
              <Sparkline
                data={ZONE7_SHARE_SERIES}
                color={CHART_PALETTE.critical}
              />
              <MixShiftDelta>12% → 35%</MixShiftDelta>
            </MixShiftRow>
            <MixShiftRow>
              <MixShiftLabel>Avg package weight</MixShiftLabel>
              <Sparkline
                data={PACKAGE_WEIGHT_SERIES}
                color={CHART_PALETTE.critical}
              />
              <MixShiftDelta>1.4 → 1.9 lb</MixShiftDelta>
            </MixShiftRow>
            <MixShiftRow>
              <MixShiftLabel>Avg ship cost / order</MixShiftLabel>
              <Sparkline
                data={SHIP_COST_SERIES}
                color={CHART_PALETTE.critical}
              />
              <MixShiftDelta>$7.20 → $11.85</MixShiftDelta>
            </MixShiftRow>
          </MixShiftGrid>
        </SectionCard>
      </TwoColRow>

      <SectionCard>
        <SectionTitle>Recommended paths · Northwind</SectionTitle>
        <SectionSubtitle>
          Three options sized from the trajectory math. Discuss with Neo opens
          the full renegotiation brief flow.
        </SectionSubtitle>
        <ActionsList>
          <ActionRow $recommended>
            <div>
              <ActionTitle>
                Renegotiate now · zone 5+ surcharge $2.50/order
              </ActionTitle>
              <ActionDescription>
                Affects ~38% of Northwind's volume. Margin recovers to 13.5%,
                +$1.4M annualized contribution restored. Risk: opening a
                contract 10 months early.
              </ActionDescription>
            </div>
            <ActionLabel $tone="good">Recommended</ActionLabel>
          </ActionRow>
          <ActionRow>
            <div>
              <ActionTitle>Wait for Mar 2027 renewal</ActionTitle>
              <ActionDescription>
                Accept ~$2.1M lost contribution over the next 10 months. Their
                west-coast growth keeps compounding the gap.
              </ActionDescription>
            </div>
            <ActionLabel $tone="neutral">Status quo</ActionLabel>
          </ActionRow>
          <ActionRow>
            <div>
              <ActionTitle>
                Cap west-coast volume at current run rate
              </ActionTitle>
              <ActionDescription>
                Hold zone 7+ share at 35% until renewal. Northwind growth path
                stalls; relationship risk.
              </ActionDescription>
            </div>
            <ActionLabel $tone="neutral">Defensive</ActionLabel>
          </ActionRow>
        </ActionsList>
      </SectionCard>
    </ShellRoot>
  )
}
